/* eslint-disable @typescript-eslint/no-explicit-any */
import { FC, memo } from 'react';
import { IntlShape, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { formatDateTime } from '../../../i18n/format';
import { Typo } from '../../../components/Typo';
import Badge from '../../../components/shared/Badge';
import Button from '../../../components/shared/Button';
import PhotoFill from '../../../components/shared/PhotoFill';
import DogeIcon from '../../../components/icons/DogeIcon';
import MessageIcon from '../../../components/icons/MessageIcon';
import CalendarIcon from '../../../components/icons/CalendarIcon';
import DistanceAway from '../../../components/shared/DistanceAway';
import { TRANSLATED_CONSTANTS } from '../../../static/translatedConstants';
import { ALTERNATE_PRICE_OPTION, LISTING_TYPES, ROUTES } from '../../../constants/data';
import { useHandleMessageSenderAuth } from '../../../hooks/useHandleMessageSenderAuth';
import { AuthUserState } from '../../../context/AuthUserProvider';
import { getListingPlaceholder } from '../../../util/listingPlaceholder';
import { useLocale } from '../../../i18n/locale';
import { localizePath } from '../../../i18n/localePath';

// Purpose-built card for the MOBILE pin-tap bottom sheet (Luke's F7 polish
// call): the desktop popup card is tall-and-narrow and looked cut off inside
// a full-width sheet. This one is horizontal - square thumb left, facts
// right, full-width action row - so its height is compact and predictable
// and the sheet never needs to scroll or clip. Desktop popups keep
// CustomPopup unchanged.

const formatEventDate = (iso?: string) => {
  if (!iso) {
    return '';
  }
  return formatDateTime(new Date(iso), {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
};

const getEventFromPrice = (intl: IntlShape, tiers?: any[]) => {
  const doge = (tiers || [])
    .map((tier) => Number(tier?.priceInDoge))
    .filter((value) => !isNaN(value) && value > 0);
  if (doge.length === 0) {
    return intl.formatMessage({
      id: 'shared.listing_card.tickets_tbd',
      defaultMessage: 'Tickets TBD'
    });
  }
  return intl.formatMessage(
    { id: 'shared.listing_card.event_from_price', defaultMessage: 'From {amount} DOGE' },
    { amount: Math.round(Math.min(...doge)) }
  );
};

const themeMap: { [key: string]: any } = {
  wanted: 'pink',
  personal: 'blue',
  storefrontItem: 'indigo',
  event: 'orange'
};

type Props = {
  listing: any;
};

const SheetListingCard: FC<Props> = ({ listing }) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { locale } = useLocale();
  const { authUser } = AuthUserState();
  const { onSendMessage } = useHandleMessageSenderAuth(listing);
  const type = listing?.type ?? 'personal';
  const isEvent = type === LISTING_TYPES.EVENT || type === 'event';
  // No-price listings (Luke's call): "Đ 0 DOGE" read like a zero price bug.
  // A listing is negotiable when the seller picked the no-price option OR
  // simply has no amount in either currency. Free stays an EVENT concept
  // (isFreeEvent renders "Free event" above) - items have no free checkbox.
  const dogeAmount = Math.round(Number(listing?.price?.priceInDoge) || 0);
  const usdAmount = Number(listing?.price?.priceInUSD) || 0;
  const isNegotiable =
    listing?.price?.alternatePriceOptions === ALTERNATE_PRICE_OPTION.NEGOTIABLE ||
    (dogeAmount <= 0 && usdAmount <= 0);
  // Tap-through (F7 round 3, Luke's bug): the whole card opens the detail
  // page, same route map CardWrapper uses for the desktop list cards. The
  // Message button below stopPropagation()s so it keeps its own behavior.
  const detailBase =
    type === 'storefrontItem' ? ROUTES.STOREFRONT_ITEM_DETAILS_ROUTE : ROUTES.LISTING_DETAILS_ROUTE;
  const openListing = () => {
    if (!listing?.id) {
      return;
    }
    navigate(localizePath(`${detailBase}/${listing.id}`, locale));
  };
  const image = listing?.image?.length > 0 ? listing.image[0].url : getListingPlaceholder(type);
  const typeLabelMap: { [key: string]: string } = {
    wanted: intl.formatMessage({
      id: TRANSLATED_CONSTANTS.WANTED.id,
      defaultMessage: TRANSLATED_CONSTANTS.WANTED.defaultMessage
    }),
    personal: intl.formatMessage({
      id: TRANSLATED_CONSTANTS.PERSONAL.id,
      defaultMessage: TRANSLATED_CONSTANTS.PERSONAL.defaultMessage
    }),
    storefrontItem: intl.formatMessage({
      id: TRANSLATED_CONSTANTS.STOREFRONT_ITEM.id,
      defaultMessage: TRANSLATED_CONSTANTS.STOREFRONT_ITEM.defaultMessage
    }),
    event: intl.formatMessage({
      id: TRANSLATED_CONSTANTS.EVENTS.id,
      defaultMessage: TRANSLATED_CONSTANTS.EVENTS.defaultMessage
    })
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={openListing}
      onKeyDown={(event: any) => {
        // Only when the CARD itself is focused - keydown bubbles up from the
        // Message button, and Enter there must not also navigate away.
        if (event.target !== event.currentTarget) {
          return;
        }
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          openListing();
        }
      }}
      className="w-full cursor-pointer rounded-2xl font-inter focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow-500">
      <div className="flex gap-3 p-3 pb-2">
        <div className="relative h-24 w-24 shrink-0 overflow-hidden rounded-lg bg-gray-50">
          <PhotoFill className="h-full w-full" src={image} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <Badge size="sm" theme={themeMap[type] ?? 'blue'} label={typeLabelMap[type] ?? ''} />
            <DistanceAway
              latitude={listing?.location?.latitude}
              longitude={listing?.location?.longitude}
              className="text-xs text-gray-500"
            />
          </div>
          <div title={listing?.name}>
            <Typo.textMd fontWeight="font-semibold" className="clamp-2 mt-1 text-gray-900">
              {listing?.name}
            </Typo.textMd>
          </div>
          {isEvent ? (
            <div className="mt-1 flex flex-col gap-y-0.5">
              <Typo.textXs
                fontWeight="font-semibold"
                className="flex items-center gap-x-1 text-gray-900">
                <CalendarIcon className="h-3.5 w-3.5 text-gray-500" />
                {formatEventDate(listing?.eventStartDate)}
              </Typo.textXs>
              <Typo.textXs fontWeight="font-semibold" className="text-orange-700">
                {listing?.isFreeEvent
                  ? intl.formatMessage({
                      id: 'page.create_event.mode_free',
                      defaultMessage: 'Free event'
                    })
                  : getEventFromPrice(intl, listing?.ticketTiers)}
              </Typo.textXs>
            </div>
          ) : (
            <div className="mt-1 flex items-baseline gap-x-2">
              {isNegotiable ? (
                <Typo.textMd fontWeight="font-semibold" className="text-gray-700">
                  {intl.formatMessage({
                    id: 'shared.negotiable',
                    defaultMessage: 'Negotiable'
                  })}
                </Typo.textMd>
              ) : (
                <>
                  <Typo.textMd
                    fontWeight="font-semibold"
                    className="flex items-center text-gray-900">
                    <DogeIcon className="h-4 w-4" /> {dogeAmount} DOGE
                  </Typo.textMd>
                  {usdAmount > 0 && (
                    <Typo.textXs fontWeight="font-normal" className="text-gray-600">
                      ${usdAmount.toFixed(2)}
                    </Typo.textXs>
                  )}
                </>
              )}
            </div>
          )}
          <Typo.textXs fontWeight="font-normal" className="mt-0.5 truncate text-blue-gray-600">
            {listing?.user?.name || listing?.user?.username}
          </Typo.textXs>
        </div>
      </div>
      {!isEvent && (
        <div className="px-3 pb-3">
          <Button
            type="button"
            theme="primary-yellow"
            size="sm"
            className="w-full"
            disabled={authUser?.id == listing?.user?.id}
            onClick={(event: any) => {
              event.stopPropagation();
              onSendMessage();
            }}
            icon={<MessageIcon className="text-gray-900" />}>
            <Typo.textSm fontWeight="font-semibold" className="text-gray-900">
              {type === 'wanted'
                ? intl.formatMessage({
                    id: TRANSLATED_CONSTANTS.MESSAGE_BUYER.id,
                    defaultMessage: TRANSLATED_CONSTANTS.MESSAGE_BUYER.defaultMessage
                  })
                : intl.formatMessage({
                    id: TRANSLATED_CONSTANTS.MESSAGE_SELLER.id,
                    defaultMessage: TRANSLATED_CONSTANTS.MESSAGE_SELLER.defaultMessage
                  })}
            </Typo.textSm>
          </Button>
        </div>
      )}
    </div>
  );
};

export default memo(SheetListingCard);
